import React, { useCallback, useEffect, useState } from "react";
import InputLabel from "designer/ui/smart/input-label/input-label";
import { Button } from "designer/ui/smart/button/button-style";
import { dontAwait, isValidEmailAddress } from "common/Support";
import { trackEvent } from "analytics";
import { addOrgMember, getOrganizationPersistentInviteLink } from "designer/apps/orgs/orgs-lib";
import copyToClipboard from "copy-to-clipboard";
import { OrgIcon } from "designer/apps/orgs/orgs-sidebar/org-icon";
import { useSelector } from "react-redux";
import { RootState } from "store/types";
import { styled } from "sharedUI/styles";

interface InviteMembersProps {
    onDone: () => void;
    orgID: string;
    source: string;
}

const InviteMembersStyle = styled.div`
    h1 {
        font-size: 26px;
        font-weight: 800;
        margin-top: 12px;
    }

    .skip {
        font-size: 13px;
        color: ${p => p.theme.textPale};
        justify-content: center;
        display: flex;

        &:hover {
            color: ${p => p.theme.textBase};
        }
    }
`;

const InviteMembersImpl: React.FunctionComponent<InviteMembersProps> = ({ orgID, onDone, source }) => {
    const orgs = useSelector((state: RootState) => state.organizations);
    const org = orgs.find(o => o.id === orgID);
    const orgDisplayName = org?.displayName || "";
    const [currentEmails, setCurrentEmails] = useState("");
    const [emailValid, setEmailValid] = useState(false);
    const [inviting, setInviting] = useState(false);
    const [warning, setWarning] = useState<string | undefined>(undefined);
    const [inviteLink, setInviteLink] = React.useState<string | undefined>(undefined);

    const getInviteLink = React.useCallback(
        async (createNew: boolean = true) => {
            const persistentInviteLink = await getOrganizationPersistentInviteLink(orgID, createNew);
            setInviteLink(persistentInviteLink);
        },
        [orgID]
    );

    useEffect(() => {
        dontAwait(getInviteLink());
    }, [getInviteLink]);

    const onInputChange = useCallback((evt: React.ChangeEvent<HTMLInputElement>) => {
        const value = evt.target.value;
        setCurrentEmails(value);
        setWarning(undefined);

        const cleanedEmails = value.split(",").map(v => v.trim());
        const isValidEmail = cleanedEmails.every(isValidEmailAddress);

        setEmailValid(isValidEmail);
    }, []);

    const sendInvites = useCallback(
        async (emailsToSend: string[]) => {
            setInviting(true);
            const resp = addOrgMember(orgID, emailsToSend);
            trackEvent("sign up invite members", { count: emailsToSend.length });
            trackEvent("org_member_invited", { org_id: orgID, source: source });
            setInviting(false);

            if (resp === undefined) {
                setWarning("Unable to invite user. Please try again");
                return;
            }
        },
        [orgID, source]
    );

    const copyInviteMembersLink = React.useCallback(() => {
        if (inviteLink !== undefined) {
            copyToClipboard(inviteLink);
        }
    }, [inviteLink]);

    const validateAndSendInvites = useCallback(async () => {
        const cleanedEmails = currentEmails.split(",").map(v => v.trim());
        const isValidEmail = cleanedEmails.every(isValidEmailAddress);

        if (!isValidEmail) {
            return;
        }

        await sendInvites(cleanedEmails);
        onDone();
    }, [currentEmails, sendInvites, onDone]);

    const onKeyPress = useCallback(
        async (event: React.KeyboardEvent<HTMLInputElement>) => {
            if (event.key === "Enter") {
                await validateAndSendInvites();
            }
        },
        [validateAndSendInvites]
    );

    const inviteText = inviting ? "Sending Invitations…" : "Invite & Continue";

    return (
        <InviteMembersStyle>
            <hgroup>
                <OrgIcon size={"xlarge"} label={orgDisplayName} />
                <h1>Add team members</h1>
                <small>Invite people to work on apps together.</small>
            </hgroup>
            <div className="email-input field">
                <label className="invite-label">Invite via email</label>
                <InputLabel
                    margin={8}
                    autoFocus={true}
                    focusBorder={true}
                    warning={warning}
                    label={undefined}
                    placeholder="Enter emails (separated by commas)"
                    onChange={onInputChange}
                    onKeyPress={onKeyPress}
                    tallerVariation={true}
                />
            </div>
            <div className="email-send-button">
                <Button
                    disabled={inviting || !emailValid}
                    variant="filled"
                    inline={true}
                    type="button"
                    onClick={validateAndSendInvites}
                    width={82}
                    tallerVariation={true}>
                    {inviteText}
                </Button>
            </div>
            <div className="invite-link">
                <Button
                    variant="tertiary"
                    disabled={inviteLink === undefined}
                    onClick={copyInviteMembersLink}
                    tallerVariation={true}>
                    Copy Invite Link
                </Button>
            </div>
            <div className="skip">
                <button onClick={onDone}>Skip</button>
            </div>
        </InviteMembersStyle>
    );
};

export default InviteMembersImpl;
